import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, RotateCcw, ShieldCheck, Minus, Plus } from "lucide-react";
import { api, errMsg, money, track, dt, fileUrl } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { btnGold, btnGhost } from "../../components/store/ui";
import { VariantPicker } from "../../components/store/VariantPicker";
import { Reviews } from "../../components/store/Reviews";

export default function ProductPage() {
  const { id } = useParams();
  const { locale, t, setCart } = useApp();
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`/store/products/${id}`, { params: { market: locale.market } }).then((r) => {
      setP(r.data);
      document.title = `${r.data.title} — À l'abri`;
      const first = r.data.variants.find((x) => x.in_stock) || r.data.variants[0];
      setVariant(first?.id);
      const ci = first ? r.data.images.findIndex((u) => r.data.image_colors?.[u] === first.name.split(" · ")[0]) : -1;
      setImg(Math.max(0, ci));
    }).catch(() => setP(false));
    track("view_product", { product_id: id, market: locale.market });
    return () => { document.title = "À l'abri — Objets choisis pour la maison"; };
  }, [id, locale.market]);
  if (p === false) return <div className="p-16" data-testid="product-not-found">Produit introuvable. <Link to="/shop" className="underline">Boutique</Link></div>;
  if (!p) return <div className="p-16 label-xs">Chargement…</div>;
  const v = p.variants.find((x) => x.id === variant);
  const pick = (x) => {
    if (!x) { return; }
    setVariant(x.id);
    const c = x.name.split(" · ")[0];
    const i = p.images.findIndex((u) => p.image_colors?.[u] === c);
    if (i >= 0) setImg(i);
  };
  const add = async (buyNow = false) => {
    try {
      const { data } = await api.post("/cart/items", { product_id: p.id, variant_id: variant, qty }, { params: { market: locale.market } });
      setCart(data);
      track("add_to_cart", { product_id: p.id, market: locale.market });
      if (buyNow) { nav("/checkout"); return; }
      toast.success("Ajouté au panier");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const c = p.content || {};
  return (
    <>
    <section className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7 rise min-w-0">
        <div className="aspect-square bg-[#14161D] overflow-hidden"><img src={fileUrl((p.images.length ? p.images : [p.image])[img] || p.image, 1200)} alt={p.title} decoding="async" className="w-full h-full object-cover" data-testid="product-main-image" /></div>
        {p.images.length > 1 && <div className="flex gap-3 mt-3 overflow-x-auto pb-1">{p.images.map((u, i) => <button key={u} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)} className={`w-20 h-20 shrink-0 overflow-hidden border ${i === img ? "border-[#C9A96E]" : "border-white/10"}`}><img src={fileUrl(u, 300)} loading="lazy" alt="" className="w-full h-full object-cover" /></button>)}</div>}
      </div>
      <div className="lg:col-span-5 rise min-w-0" style={{ animationDelay: "100ms" }}>
        {p.brand && <Link to={`/brand/${p.brand.slug}`} className="label-xs text-[#C9A96E]">{p.brand.name}</Link>}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3" data-testid="product-title">{p.title}</h1>
        {c.hook && <p className="font-serif-edit italic text-lg text-muted-foreground mt-4">{c.hook}</p>}
        <div className="mt-6 flex items-baseline gap-3">
          <span className="font-mono text-3xl" data-testid="product-detail-price">{money(v?.price ?? p.price, p.currency, locale.lang)}</span>
          <span className="text-xs text-muted-foreground">{p.tax_inclusive ? t("vat_incl") : "+ taxes selon la province"} · {t("shipping_incl")}</span>
        </div>
        <div className="mt-8">
          <VariantPicker p={p} variant={variant} onPick={pick} />
          {v && <div className="text-xs mt-2 font-mono" data-testid="variant-stock-state">{!v.in_stock ? t("out_of_stock") : v.low_stock ? t("low_stock") : t("in_stock")}</div>}
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <div className="flex items-center border border-white/10">
            <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center font-mono" data-testid="qty-value">{qty}</span>
            <button data-testid="qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="p-3"><Plus className="w-4 h-4" /></button>
          </div>
          <button data-testid="add-to-cart-button" disabled={!p.available || !v?.in_stock} onClick={() => add(false)} className={`${btnGhost} flex-1`}>
            {p.available ? t("add_to_cart") : p.unavailable_reason}
          </button>
          {p.available && <button data-testid="buy-now-button" disabled={!v?.in_stock} onClick={() => add(true)} className={`${btnGold} flex-1`}>Commander</button>}
        </div>
        <div className="mt-8 space-y-3 text-sm border-t border-white/5 pt-6">
          <div className="flex gap-3"><Truck className="w-4 h-4 text-[#C9A96E]" /> {t("delivery")} : {p.delivery.days_min}–{p.delivery.days_max} {t("days")}</div>
          <div className="flex gap-3"><RotateCcw className="w-4 h-4 text-[#C9A96E]" /> {t("returns", { n: p.return_days })}</div>
          <div className="flex gap-3"><ShieldCheck className="w-4 h-4 text-[#C9A96E]" /> Paiement sécurisé Stripe · disponibilité vérifiée {dt(p.last_sync_at)}</div>
        </div>
        <div className="mt-10">
          <div className="label-xs mb-3">{t("features")}</div>
          <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4" data-testid="product-features">{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
          {c.description && <p className="mt-6 text-sm leading-relaxed text-muted-foreground" data-testid="product-description">{c.description}</p>}
          {c.benefits?.length > 0 && <ul className="mt-4 text-sm space-y-1">{c.benefits.map((b) => <li key={b}>— {b}</li>)}</ul>}
        </div>
        {c.faq?.length > 0 && (
          <div className="mt-10">
            <div className="label-xs mb-3">{t("faq")}</div>
            {c.faq.map((f) => <details key={f.q} className="border-b border-white/5 py-3 text-sm"><summary className="cursor-pointer">{f.q}</summary><p className="text-muted-foreground mt-2">{f.a}</p></details>)}
          </div>
        )}
      </div>
    </section>
    <Reviews pid={p.id} />
    </>
  );
}
